import { useMemo, useState, type SyntheticEvent } from 'react';
import {
  Autocomplete,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  createFilterOptions,
  type AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete';
import { styles } from './styles';

type ServiceIdOption = { label: string; value: string };

const SELECT_ALL_VALUE = '__select_all__';
const selectAllOption: ServiceIdOption = {
  label: 'Select All',
  value: SELECT_ALL_VALUE,
};
const defaultFilterOptions = createFilterOptions<ServiceIdOption>();

/** Display name inside trailing `(…)` from labels like `id (name)`. */
function extractParenName(label: string): string | null {
  const m = label.match(/\(([^)]+)\)\s*$/);
  return m ? m[1].trim() : null;
}

/**
 * Resolves a pasted token only when unambiguous: exact service id, exact full
 * label, or exactly one option whose parenthetical display name matches.
 */
function resolvePastedToken(
  token: string,
  options: ServiceIdOption[],
): ServiceIdOption | null {
  const t = token.trim();
  if (!t) return null;
  const lower = t.toLowerCase();

  const byValue = options.filter((o) => o.value.toLowerCase() === lower);
  if (byValue.length === 1) return byValue[0];
  if (byValue.length > 1) return null;

  const byFullLabel = options.filter((o) => o.label.toLowerCase() === lower);
  if (byFullLabel.length === 1) return byFullLabel[0];
  if (byFullLabel.length > 1) return null;

  const byParenName = options.filter((o) => {
    const name = extractParenName(o.label);
    return name !== null && name.toLowerCase() === lower;
  });
  if (byParenName.length === 1) return byParenName[0];

  return null;
}

type BulkServiceIdSelectProps = {
  serviceIdOptions: ServiceIdOption[];
  selectedIds: string;
  onChange: (values: string[]) => void;
};

const BulkServiceIdSelect = ({
  serviceIdOptions,
  selectedIds,
  onChange,
}: BulkServiceIdSelectProps) => {
  const [warningMessage, setWarningMessage] = useState('');
  const [inputValue, setInputValue] = useState('');

  const selectedArray = useMemo(
    () => (selectedIds ? selectedIds.split(',').filter(Boolean) : []),
    [selectedIds],
  );

  const filteredOptions = useMemo(
    () =>
      defaultFilterOptions(serviceIdOptions, {
        inputValue,
        getOptionLabel: (option: ServiceIdOption) => option.label || '',
      }),
    [serviceIdOptions, inputValue],
  );

  const scopeIds = useMemo(
    () => filteredOptions.map((o) => o.value),
    [filteredOptions],
  );

  const countSelectedInScope = useMemo(
    () => scopeIds.filter((id) => selectedArray.includes(id)).length,
    [scopeIds, selectedArray],
  );

  const allScopeSelected =
    scopeIds.length > 0 && countSelectedInScope === scopeIds.length;
  const someScopeSelected =
    scopeIds.length > 0 &&
    countSelectedInScope > 0 &&
    countSelectedInScope < scopeIds.length;

  const selectedOptions = useMemo(
    () => serviceIdOptions.filter((opt) => selectedArray.includes(opt.value)),
    [serviceIdOptions, selectedArray],
  );

  const handleAutocompleteChange = (
    _event: SyntheticEvent,
    newValue: ServiceIdOption[],
  ) => {
    setWarningMessage('');
    const clickedSelectAll = newValue.some((v) => v.value === SELECT_ALL_VALUE);

    if (clickedSelectAll) {
      if (allScopeSelected) {
        onChange(selectedArray.filter((id) => !scopeIds.includes(id)));
      } else {
        onChange([...new Set([...selectedArray, ...scopeIds])]);
      }
    } else {
      onChange(newValue.map((v) => v.value));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const trimmed = e.clipboardData.getData('text').trim();
    if (!trimmed) return;

    const hasComma = trimmed.includes(',');
    const pastedItems = hasComma
      ? trimmed
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [trimmed];

    if (!hasComma && pastedItems.length === 1) {
      const match = resolvePastedToken(pastedItems[0], serviceIdOptions);
      if (!match) return;
    }

    e.preventDefault();
    setWarningMessage('');

    const matchedValues = new Set(selectedArray);
    const unmatchedItems: string[] = [];

    pastedItems.forEach((item) => {
      const match = resolvePastedToken(item, serviceIdOptions);
      if (match) {
        matchedValues.add(match.value);
      } else {
        unmatchedItems.push(item);
      }
    });

    onChange(Array.from(matchedValues));

    if (unmatchedItems.length > 0) {
      setWarningMessage(
        'One or more items is not present in the list but you can still proceed with Silence',
      );
    }
  };

  const handleInputChange = (
    _event: SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    setInputValue(newInputValue);
    if (reason === 'input') {
      setWarningMessage('');
    }
  };

  return (
    <Stack sx={{ mt: 3, width: '100%' }}>
      <Typography sx={{ ...styles.formInput, mb: 1 }} variant="subtitle2">
        Service Ids
      </Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={serviceIdOptions}
        value={selectedOptions}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleAutocompleteChange}
        getOptionLabel={(option) => option.label || ''}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        filterOptions={() => {
          if (filteredOptions.length === 0) return [];
          return [selectAllOption, ...filteredOptions];
        }}
        renderOption={(props, option, { selected }) => {
          const isSelectAll = option.value === SELECT_ALL_VALUE;
          return (
            <li
              {...props}
              key={option.value}
              style={{
                ...(typeof props.style === 'object' ? props.style : {}),
                ...(isSelectAll ? { borderBottom: '1px solid #e0e0e0' } : {}),
              }}
            >
              <Checkbox
                checked={isSelectAll ? allScopeSelected : selected}
                indeterminate={isSelectAll ? someScopeSelected : false}
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" fontWeight={isSelectAll ? 600 : 400}>
                {option.label}
              </Typography>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Search, or paste one ID/name or comma-separated values"
            onPaste={handlePaste}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                display: 'none',
              },
              '& .MuiInputBase-root': {
                maxHeight: 250,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'action.disabled',
                borderRadius: 1,
                '&:hover': {
                  borderColor: 'text.primary',
                },
                '&.Mui-focused': {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                },
              },
            }}
          />
        )}
      />
      {warningMessage && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
          {warningMessage}
        </Typography>
      )}
    </Stack>
  );
};

export default BulkServiceIdSelect;
